/* eslint-disable no-alert */
import React, { useState, useEffect, useCallback } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import {
  useGetZellerAdsStatusQuery,
  useLazyGetZellerAdsDebugConfigQuery,
  useLazyGetZellerAdsAuthUrlQuery,
  useLazyGetZellerAdsAdAccountsQuery,
  useLazyGetZellerAdsSpecificAdAccountQuery,
  useLazyGetZellerAdsCampaignsQuery,
  useLazyGetZellerAdsAdSetsQuery,
  useLazyGetZellerAdsAdsQuery,
  useLazyGetZellerAdsAdSetsFromCampaignQuery,
  useLazyGetZellerAdsAdsFromAdSetQuery,
  useDisconnectZellerAdsMutation,
  useGetPendingAdAccountsQuery,
  useGetAvailableBusinessUnitsQuery,
  useAssignAdAccountsMutation,
  useLazyGetZellerAdsAdAccountsSyncQuery,
  useSyncAllMetaDataMutation,
} from '../../services/api/zellerAds.api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'white',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1.25),
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1877f2',
  color: 'white',
  padding: theme.spacing(1.5, 3),
  margin: theme.spacing(1, 0.5),
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#166fe5',
  },
  '&.danger': {
    backgroundColor: '#dc3545',
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
}));

const StatusAlert = styled(Alert)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(0.625),
  margin: theme.spacing(2, 0),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const ResultPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(0.625),
  margin: theme.spacing(2, 0),
  fontFamily: 'monospace',
  fontSize: '14px',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
}));

interface ZellerAdsState {
  adAccountId: string;
  adSetId: string;
  campaignId: string;
  adId: string;
  datePreset: string;
  limitResults: string;
  status: string;
  result: unknown;
  isError: boolean;
}

interface AdAccountAssignment {
  adAccountId: string;
  businessUnitId: number;
}

const ZellerAdsConfig: React.FC = () => {
  const [state, setState] = useState<ZellerAdsState>({
    adAccountId: '',
    adSetId: '',
    campaignId: '',
    adId: '',
    datePreset: 'last_7d',
    limitResults: '',
    status: 'Verificando...',
    result: undefined,
    isError: false,
  });

  const [assignments, setAssignments] = useState<AdAccountAssignment[]>([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  const { data: statusData, isLoading: statusLoading, error: statusError } = useGetZellerAdsStatusQuery();
  const { data: pendingAdAccounts, refetch: refetchPendingAdAccounts } = useGetPendingAdAccountsQuery();
  const { data: businessUnits } = useGetAvailableBusinessUnitsQuery();
  const [assignAdAccounts, { isLoading: assignLoading }] = useAssignAdAccountsMutation();

  const [getDebugConfig, { isLoading: debugLoading }] = useLazyGetZellerAdsDebugConfigQuery();
  const [getAuthUrl, { isLoading: authUrlLoading }] = useLazyGetZellerAdsAuthUrlQuery();
  const [getAdAccountsSync, { isLoading: adAccountsSyncLoading }] = useLazyGetZellerAdsAdAccountsSyncQuery();
  const [getAdAccounts, { isLoading: adAccountsLoading }] = useLazyGetZellerAdsAdAccountsQuery();
  const [getSpecificAdAccount, { isLoading: specificAdAccountLoading }] = useLazyGetZellerAdsSpecificAdAccountQuery();
  const [getCampaigns, { isLoading: campaignsLoading }] = useLazyGetZellerAdsCampaignsQuery();
  const [getAdSets, { isLoading: adSetsLoading }] = useLazyGetZellerAdsAdSetsQuery();
  const [getAds, { isLoading: adsLoading }] = useLazyGetZellerAdsAdsQuery();
  const [getAdSetsFromCampaign, { isLoading: adSetsFromCampaignLoading }] =
    useLazyGetZellerAdsAdSetsFromCampaignQuery();
  const [getAdsFromAdSet, { isLoading: adsFromAdSetLoading }] = useLazyGetZellerAdsAdsFromAdSetQuery();
  const [disconnectMutation, { isLoading: disconnectLoading }] = useDisconnectZellerAdsMutation();
  const [syncAllMetaData, { isLoading: syncMetaLoading }] = useSyncAllMetaDataMutation();

  const isAnyLoading =
    statusLoading ||
    debugLoading ||
    authUrlLoading ||
    adAccountsLoading ||
    specificAdAccountLoading ||
    campaignsLoading ||
    adSetsLoading ||
    adsLoading ||
    adSetsFromCampaignLoading ||
    adsFromAdSetLoading ||
    disconnectLoading ||
    adAccountsSyncLoading ||
    assignLoading ||
    syncMetaLoading;

  const updateResult = useCallback((data: unknown, isError = false) => {
    setState((prev) => ({
      ...prev,
      result: data,
      isError: Boolean(isError),
    }));
  }, []);

  const executeQuery = useCallback(
    async (queryFn: () => { unwrap: () => Promise<unknown> }) => {
      try {
        const result = await queryFn().unwrap();
        updateResult(result);
      } catch (error) {
        updateResult({ error: error || 'Error desconocido' }, true);
      }
    },
    [updateResult]
  );

  const handleDebugConfig = useCallback(() => {
    executeQuery(getDebugConfig).catch(() => {});
  }, [executeQuery, getDebugConfig]);

  const handleStartOAuth = useCallback(() => {
    const startOAuth = async () => {
      try {
        const result = await getAuthUrl().unwrap();
        if (result.success && result.authUrl) {
          window.location.href = result.authUrl;
        } else {
          updateResult({ error: 'No se recibió URL de autorización válida', data: result }, true);
        }
      } catch (error) {
        updateResult({ error: error || 'Error al obtener URL de autorización' }, true);
      }
    };

    startOAuth().catch(() => {});
  }, [getAuthUrl, updateResult]);

  const handleGetAuthUrl = useCallback(() => {
    executeQuery(getAuthUrl).catch(() => {});
  }, [executeQuery, getAuthUrl]);

  const handleGetAdAccountsSync = useCallback(() => {
    executeQuery(getAdAccountsSync).catch(() => {});
  }, [executeQuery, getAdAccountsSync]);

  const handleGetAdAccounts = useCallback(() => {
    executeQuery(getAdAccounts).catch(() => {});
  }, [executeQuery, getAdAccounts]);

  const handleGetSpecificAdAccount = useCallback(() => {
    if (!state.adAccountId.trim()) {
      alert('Por favor, ingresa un ID de cuenta publicitaria');
      return;
    }
    executeQuery(() => getSpecificAdAccount(state.adAccountId)).catch(() => {});
  }, [executeQuery, getSpecificAdAccount, state.adAccountId]);

  const handleGetCampaigns = useCallback(() => {
    if (!state.adAccountId.trim()) {
      alert('Por favor, ingresa un ID de cuenta publicitaria');
      return;
    }
    executeQuery(() =>
      getCampaigns({
        adAccountId: state.adAccountId,
        limit: state.limitResults || undefined,
      })
    ).catch(() => {});
  }, [executeQuery, getCampaigns, state.adAccountId, state.limitResults]);

  const handleGetAdSets = useCallback(() => {
    if (!state.adAccountId.trim()) {
      alert('Por favor, ingresa un ID de cuenta publicitaria');
      return;
    }
    executeQuery(() =>
      getAdSets({
        adAccountId: state.adAccountId,
        limit: state.limitResults || undefined,
      })
    ).catch(() => {});
  }, [executeQuery, getAdSets, state.adAccountId, state.limitResults]);

  const handleGetAds = useCallback(() => {
    if (!state.adAccountId.trim()) {
      alert('Por favor, ingresa un ID de cuenta publicitaria');
      return;
    }
    executeQuery(() =>
      getAds({
        adAccountId: state.adAccountId,
        limit: state.limitResults || undefined,
      })
    ).catch(() => {});
  }, [executeQuery, getAds, state.adAccountId, state.limitResults]);

  const handleGetAdSetsFromCampaign = useCallback(() => {
    if (!state.campaignId.trim()) {
      alert('Por favor, ingresa un ID de Campaign');
      return;
    }
    executeQuery(() =>
      getAdSetsFromCampaign({
        campaignId: state.campaignId,
        limit: state.limitResults || undefined,
      })
    ).catch(() => {});
  }, [executeQuery, getAdSetsFromCampaign, state.campaignId, state.limitResults]);

  const handleGetAdsFromAdSet = useCallback(() => {
    if (!state.adSetId.trim()) {
      alert('Por favor, ingresa un ID de AdSet');
      return;
    }
    executeQuery(() =>
      getAdsFromAdSet({
        adSetId: state.adSetId,
        limit: state.limitResults || undefined,
      })
    ).catch(() => {});
  }, [executeQuery, getAdsFromAdSet, state.adSetId, state.limitResults]);

  const handleDisconnect = useCallback(() => {
    if (window.confirm('¿Estás seguro de que quieres desconectar tu cuenta de Meta?')) {
      executeQuery(disconnectMutation).catch(() => {});
    }
  }, [executeQuery, disconnectMutation]);

  /**
   * Maneja la sincronización de toda la información de Meta
   * Muestra un diálogo de confirmación antes de ejecutar la sincronización
   * ya que es un proceso que puede tomar varios minutos
   */
  const handleSyncAllMetaData = useCallback(() => {
    if (
      window.confirm(
        '¿Estás seguro de que quieres sincronizar toda la información de Meta? Este proceso puede tomar varios minutos.'
      )
    ) {
      executeQuery(syncAllMetaData).catch(() => {});
    }
  }, [executeQuery, syncAllMetaData]);

  const handleAssignAdAccounts = useCallback(async () => {
    if (assignments.length === 0) {
      alert('Por favor, asigna al menos una ad account a una business unit');
      return;
    }

    try {
      const result = await assignAdAccounts({ assignments }).unwrap();
      updateResult(result);
      setAssignments([]);
      setShowAssignmentModal(false);
      refetchPendingAdAccounts().catch(() => {});
    } catch (error) {
      updateResult({ error: error || 'Error al asignar ad accounts' }, true);
    }
  }, [assignments, assignAdAccounts, updateResult, refetchPendingAdAccounts]);

  const updateAssignment = useCallback((adAccountId: string, businessUnitId: number) => {
    setAssignments((prev) => {
      const existing = prev.find((a) => a.adAccountId === adAccountId);
      if (existing) {
        return prev.map((a) => (a.adAccountId === adAccountId ? { ...a, businessUnitId } : a));
      }
      return [...prev, { adAccountId, businessUnitId }];
    });
  }, []);

  const handleShowAssignmentModal = useCallback(() => {
    if (pendingAdAccounts?.pendingAdAccounts.length === 0) {
      alert('No hay ad accounts pendientes de asignación');
      return;
    }
    setShowAssignmentModal(true);
  }, [pendingAdAccounts]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const userName = urlParams.get('user_name');
    const adAccounts = urlParams.get('ad_accounts');

    if (success === 'true') {
      updateResult({
        success: true,
        message: `¡OAuth exitoso! Conectado como ${userName}`,
        adAccountsCount: adAccounts,
        timestamp: new Date().toISOString(),
      });
      setState((prev) => ({
        ...prev,
        status: `✅ Conectado como ${userName} (${adAccounts} cuentas publicitarias)`,
      }));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      updateResult(
        {
          error,
          error_description: errorDescription,
          timestamp: new Date().toISOString(),
        },
        true
      );
      setState((prev) => ({
        ...prev,
        status: `❌ Error en OAuth: ${error}`,
      }));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [updateResult]);

  useEffect(() => {
    if (statusData) {
      const message = statusData.connected
        ? `✅ Conectado como ${statusData.user || 'Usuario'} (${statusData.accounts || 0} cuentas publicitarias)`
        : '❌ No conectado a Meta';

      setState((prev) => ({
        ...prev,
        status: message,
      }));
    } else if (statusError) {
      setState((prev) => ({
        ...prev,
        status: '❌ Error al verificar estado',
      }));
      updateResult({ error: statusError }, true);
    }
  }, [statusData, statusError, updateResult]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <StyledPaper>
        <Typography variant="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🚀 Zeller Ads Configuración - OAuth
        </Typography>

        <StatusAlert severity={state.isError ? 'error' : 'info'}>
          <strong>Estado:</strong> {state.status}
        </StatusAlert>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ad Account ID (para consulta específica)"
              value={state.adAccountId}
              onChange={(e) => setState((prev) => ({ ...prev, adAccountId: e.target.value }))}
              placeholder="ID de cuenta publicitaria (ej: act_123456789)..."
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="AdSet ID (para consulta específica)"
              value={state.adSetId}
              onChange={(e) => setState((prev) => ({ ...prev, adSetId: e.target.value }))}
              placeholder="ID de AdSet (ej: 23846...)..."
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Campaign ID (para consulta específica)"
              value={state.campaignId}
              onChange={(e) => setState((prev) => ({ ...prev, campaignId: e.target.value }))}
              placeholder="ID de Campaign (ej: 23847...)..."
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ad ID (para consulta específica)"
              value={state.adId}
              onChange={(e) => setState((prev) => ({ ...prev, adId: e.target.value }))}
              placeholder="ID de Ad (ej: 23848...)..."
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Date Preset (para insights)</InputLabel>
              <Select
                value={state.datePreset}
                onChange={(e) => setState((prev) => ({ ...prev, datePreset: e.target.value }))}
                label="Date Preset (para insights)"
              >
                <MenuItem value="last_7d">Últimos 7 días</MenuItem>
                <MenuItem value="last_14d">Últimos 14 días</MenuItem>
                <MenuItem value="last_30d">Últimos 30 días</MenuItem>
                <MenuItem value="last_90d">Últimos 90 días</MenuItem>
                <MenuItem value="this_month">Este mes</MenuItem>
                <MenuItem value="last_month">Mes pasado</MenuItem>
                <MenuItem value="last_quarter">Último trimestre</MenuItem>
                <MenuItem value="last_year">Último año</MenuItem>
                <MenuItem value="maximum">Máximo</MenuItem>
                <MenuItem value="today">Hoy</MenuItem>
                <MenuItem value="yesterday">Ayer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Límite de resultados (opcional)"
              value={state.limitResults}
              onChange={(e) => setState((prev) => ({ ...prev, limitResults: e.target.value }))}
              placeholder="25"
              inputProps={{ min: 1, max: 100 }}
              variant="outlined"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          <StyledButton onClick={handleDebugConfig} disabled={isAnyLoading}>
            Debug Config
          </StyledButton>
          <StyledButton onClick={handleStartOAuth} disabled={isAnyLoading}>
            Iniciar OAuth
          </StyledButton>
          <StyledButton onClick={handleGetAuthUrl} disabled={isAnyLoading}>
            Obtener URL de Auth
          </StyledButton>
          <StyledButton onClick={handleGetAdAccountsSync} disabled={isAnyLoading}>
            Sync Cuentas Publicitarias
          </StyledButton>
          <StyledButton onClick={handleGetAdAccounts} disabled={isAnyLoading}>
            Obtener Cuentas Publicitarias
          </StyledButton>
          <StyledButton onClick={handleGetSpecificAdAccount} disabled={isAnyLoading}>
            Obtener Cuenta Específica
          </StyledButton>
          <StyledButton onClick={handleGetCampaigns} disabled={isAnyLoading}>
            Obtener Campaigns
          </StyledButton>
          <StyledButton onClick={handleGetAdSets} disabled={isAnyLoading}>
            Obtener AdSets
          </StyledButton>
          <StyledButton onClick={handleGetAds} disabled={isAnyLoading}>
            Obtener Ads
          </StyledButton>
          <StyledButton onClick={handleGetAdSetsFromCampaign} disabled={isAnyLoading}>
            Obtener AdSets de Campaign
          </StyledButton>
          <StyledButton onClick={handleGetAdsFromAdSet} disabled={isAnyLoading}>
            Obtener Ads de AdSet
          </StyledButton>
          <StyledButton onClick={handleDisconnect} disabled={isAnyLoading} className="danger">
            Desconectar
          </StyledButton>
          {pendingAdAccounts?.pendingAdAccounts && pendingAdAccounts.pendingAdAccounts.length > 0 && (
            <StyledButton
              onClick={handleShowAssignmentModal}
              disabled={isAnyLoading}
              sx={{ backgroundColor: '#28a745' }}
            >
              Asignar Ad Accounts ({pendingAdAccounts.pendingAdAccounts.length})
            </StyledButton>
          )}
          <StyledButton onClick={handleSyncAllMetaData} disabled={isAnyLoading}>
            Sincronizar Toda la Información
          </StyledButton>
        </Box>

        {showAssignmentModal && (
          <Card sx={{ mb: 3, border: '2px solid #28a745' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#28a745' }}>
                Asignar Ad Accounts a Business Units
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona la business unit para cada ad account:
              </Typography>

              {pendingAdAccounts?.pendingAdAccounts.map((adAccount) => (
                <Box key={adAccount.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                  <Typography variant="h6">{adAccount.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {adAccount.id} | Estado: {adAccount.accountStatus} | Moneda: {adAccount.currency}
                  </Typography>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Business Unit</InputLabel>
                    <Select
                      value={assignments.find((a) => a.adAccountId === adAccount.id)?.businessUnitId || ''}
                      onChange={(e) => updateAssignment(adAccount.id, Number(e.target.value))}
                      label="Business Unit"
                    >
                      {businessUnits?.businessUnits.map((bu) => (
                        <MenuItem key={bu.id} value={bu.id}>
                          {bu.name} ({bu.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ))}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleAssignAdAccounts().catch(() => {});
                  }}
                  disabled={isAnyLoading || assignments.length === 0}
                  sx={{ backgroundColor: '#28a745' }}
                >
                  Asignar Ad Accounts
                </Button>
                <Button variant="outlined" onClick={() => setShowAssignmentModal(false)} disabled={isAnyLoading}>
                  Cancelar
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Resultado:
            </Typography>
            {isAnyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ResultPaper>
                {state.result
                  ? JSON.stringify(state.result, null, 2)
                  : 'Haz clic en una acción para ver el resultado...'}
              </ResultPaper>
            )}
          </CardContent>
        </Card>
      </StyledPaper>
    </Container>
  );
};

export default ZellerAdsConfig;
